import React, { useState, useEffect, useMemo, use } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import Select, { MultiValue } from "react-select";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import { Office } from "types";
import { assignedOfficesValidation, emailValidation, firstNameValidation, lastNameValidation, phoneValidation, statusValidation } from "validationSchemas/common";
import { toast } from "sonner";
import { showSuccess,showError } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";


interface PasswordInputProps {
  formik: FormikProps<{ password: string }>;
}

type Teammember = {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  phone1: string;
  phone2: string;
  email: string;
  assignedOffices: Office[]; // Array of office IDs
  permissions: string[]; // Array of permission strings
  profilePic?: string;
  statuss: string;
  licenseType: string;
  accessLevel?: string[]; // Array of access level strings
};

// type Office = {
//   id: string;
//   officeName: string;
//   officeType: string;
// };

type SelectOption = {
  value: string;
  label: string;
};


const SignageBrokerageEditTeammember = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const { teammemberId } = useParams<{ teammemberId: string }>();
  const [teammember, setTeammember] = useState<Teammember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<SelectOption[]>([]);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [validation, setValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  useEffect(() => {
    const fetchTeammemberData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${teammemberId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (data) {
          setTeammember({
            id: data.id,
            firstName: data.firstName,
            lastName: data.lastName,
            jobTitle: data.jobTitle,
            phone1: data.phone1,
            phone2: data.phone2,
            email: data.email,
            assignedOffices: data.offices || [],
            permissions: data.accessLevel || [],
            profilePic: data.profilePic || "",
            statuss: data.status || "Active",
            licenseType: data.licenseType || "",
          });
        } else {
          throw new Error("Incomplete data");
        }
      } catch (error) {
        setError("Error fetching teammember data");
        console.error(error);
      }
    };

    if (teammemberId) fetchTeammemberData();
  }, [teammemberId, token]);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevVisibility) => !prevVisibility);
  };

  const validatePassword = (password: string) => {
    setValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.handleChange(e);
    validatePassword(value);
  };

  const [showValidationBox, setShowValidationBox] = useState(false);

  const handleFocus = () => {
    setShowValidationBox(true);
  };

  const handleBlur = () => {
    setShowValidationBox(false);
  };

  // const permissionsOptions: SelectOption[] = [
  //   { value: "all", label: "All" },
  //   { value: "view", label: "View" },
  //   { value: "edit", label: "Edit" },
  //   { value: "delete", label: "Delete" },
  // ];

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setOffices(data);
        console.log("Offices data:", data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, [token]);

  const officesOptions = useMemo(
    () => offices.map((o) => ({ value: o._id, label: o.officeName })),
    [offices]
  );

  const selectedOfficeOptions = useMemo(() => {
    if (!teammember || offices.length === 0) return [];
    return teammember.assignedOffices
      .map((ao) => {
        const match = offices.find((o) => o._id === ao._id);
        return match ? { value: match._id, label: match.officeName } : null;
      })
      .filter((opt): opt is SelectOption => opt !== null);
  }, [teammember, offices]);

  // Default permissions
  const defaultPermissionsOptions: SelectOption[] = [
    { value: "all", label: "All" },
    { value: "view", label: "View" },
    { value: "edit", label: "Edit" },
    { value: "delete", label: "Delete" },
  ];

  // Combine teammember accessLevel and default options
  const combinedPermissionsOptions: SelectOption[] = useMemo(() => {
    const formValues = teammember?.accessLevel || [];
    const allValues = new Set([
      ...defaultPermissionsOptions.map((opt) => opt.value),
      ...formValues,
    ]);

    console.log('allValues:', allValues);

    return Array.from(allValues).map((value) => ({
      value,
      label:
        defaultPermissionsOptions.find((opt) => opt.value === value)?.label ||
        value.charAt(0).toUpperCase() + value.slice(1),
    }));
  }, [teammember]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      image: null as File | null,
      jobTitle: teammember?.jobTitle || "",
      firstName: teammember?.firstName || "",
      lastName: teammember?.lastName || "",
      phone: teammember?.phone1 || "",
      phone2: teammember?.phone2 || "",
      email: teammember?.email || "",
      assignedOffices: teammember?.assignedOffices?.map((o) => o._id) || [],
      permissions: teammember?.permissions || [],
      password: "",
      statuss: teammember?.statuss || "Active",
    },
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone: phoneValidation,
      email: emailValidation,
      assignedOffices: assignedOfficesValidation,
      statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

      let imageUrl = teammember?.profilePic; // fallback to current image

      if (formik.values.image) {
        // Upload the image or handle file
        const uploadedImageUrl = await uploadFile(formik.values.image); // ← implement your upload logic
        imageUrl = uploadedImageUrl;
      }


      console.log('Assigned office ids:', values.assignedOffices);
      const assignedOfficeIds = new Array();
      for (const item of values.assignedOffices) {
        assignedOfficeIds.push(item);
      }
      // console.log(assignedOfficeIds);
      // const assignedOfficeIds =
      // values.assignedOffices?.map((id) => ({
      //   id
      // })) || [];
      console.log('assignedOfficeIds:', assignedOfficeIds);
      // const assignedOfficeIds = values.assignedOffices as string[];
      const assignedOffices = new Array();
      for (const oid of assignedOfficeIds) {
        console.log(oid);
        const assignedOffice = offices?.find((office) => office._id === oid);
        assignedOffices.push({
          _id: assignedOffice?._id,
          officeName: assignedOffice?.officeName || "",
          officeType: assignedOffice?.officeType || "",
          officeAddress: {
            _id: assignedOffice?.officeAddress?.[0]?._id,
            label: assignedOffice?.officeAddress?.[0]?.label || "",
            addressLine1: assignedOffice?.officeAddress?.[0]?.addressLine1 || "",
            addressLine2: assignedOffice?.officeAddress?.[0]?.addressLine2 || "",
            city: assignedOffice?.officeAddress?.[0]?.city || "",
            state: assignedOffice?.officeAddress?.[0]?.state || "",
            country: assignedOffice?.officeAddress?.[0]?.country || "",
            zip: assignedOffice?.officeAddress?.[0]?.zip || "",
            phone: assignedOffice?.officeAddress?.[0]?.phone || "",
          }
        });
        console.log('assignedOffices:', assignedOffices);
      }

      try {
        const nextEmail = String(values.email || "").trim();
        const originalEmail = String(teammember?.email || "").trim();
        const emailChanged =
          nextEmail.toLowerCase() !== originalEmail.toLowerCase();
        const nextPassword = String(values.password || "").trim();

        const body: Record<string, unknown> = {
          userType: "team_member",
          firstName: values.firstName,
          lastName: values.lastName,
          jobTitle: values.jobTitle,
          phone1: values.phone,
          licenseType: "BROKERAGE_TEAM_MEMBER",
          status: values.statuss,
          profilePic: imageUrl,
          offices: assignedOffices,
          accessLevel: values.permissions,
          LicenseActive: true,
          created_by: {
            id: firmId,
            name: "Brokerage Firm",
          },
        };
        if (emailChanged) {
          body.email = nextEmail;
        }
        if (nextPassword) {
          body.password = nextPassword;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${teammemberId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          }
        );

     
        const result = await response.json();
           if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Teammember Updated successfully!");
        navigate(`/signage/brokerageFirm/${firmId}`);
      } catch (err) {
        console.error(err instanceof Error ? err.message : "Unknown error");
        toast.error("Failed to add Teammember.");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // Handle Assign Offices Change
  const handleOfficeChange = (options: MultiValue<SelectOption>) => {
    const ids = options.map((o) => o.value);
    formik.setFieldValue("assignedOffices", ids);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue("image", file); // Save the file object in Formik
      setPreview(URL.createObjectURL(file)); // Set preview for display
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "profiles");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.onload = () => {
        console.log('xhr:', xhr);
        if (xhr.readyState === 4) {
          const response = JSON.parse(xhr.responseText);
          console.log('upload response:', response);

          // Construct the full image URL using the base URL and `path` field
          const imageUrl = `${response.path}`;
          resolve(imageUrl);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  useEffect(() => {
    console.log("combinedPermissionsOptions:", combinedPermissionsOptions);
    console.log("teammember permissions:", teammember?.accessLevel);
    console.log("formik values permissions:", formik.values.permissions);
  }, [combinedPermissionsOptions]);

  return (
    <div className="content">
      <div className="col-md-12 d-flex justify-content-center">
        <div className="page-container mt-3 col-md-9">
          <h3 className="m-0 text-center fw-bold addheaders">Edit Brokerage Team Member </h3>
        </div>
      </div>
      {/* <h2 className="d-flex justify-content-center">Add Team Member </h2> */}

      <div className="row ms-0">
        <div className="col-md-12 d-flex justify-content-center" id="row-overlap">
          <div className="col-md-9">
            <div className="addheaders-content mt-1 card-bg-fill">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                        required
                      />
                      {formik.touched.firstName &&
                        formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                        required
                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Role{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="jobTitle"
                        name="jobTitle"
                        required
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.jobTitle}
                      >
                        <option value="">- Select -</option>
                        <option value="Manager">Manager</option>
                        <option value="Admin">Admin</option>
                        <option value="VP">VP</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Legal">Legal</option>
                        <option value="Custom">Custom</option>
                      </select>
                      {formik.touched.jobTitle && formik.errors.jobTitle ? (
                        <div className="text-danger">
                          {formik.errors.jobTitle}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone}
                        required
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>

                    {/* <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        required
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Password
                      </label>
                      <div className="position-relative auth-pass-inputgroup">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          className="form-control pe-5 password-input"
                          id="password"
                          name="password"
                          onChange={handlePasswordChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          value={formik.values.password}
                          aria-describedby="password"
                        // pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"

                        />
                        <button
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
                          type="button"
                          id="password-addon"
                          onClick={togglePasswordVisibility}
                        >
                          <i
                            className={
                              passwordVisible
                                ? "ri-eye-off-fill align-middle"
                                : "ri-eye-fill align-middle"
                            }
                          ></i>
                        </button>
                        {formik.touched.password &&
                          formik.errors.password ? (
                          <div className="text-danger">
                            {formik.errors.password}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {showValidationBox && (
                      <div className="password-validation-box mt-2">
                        <p>Password must contain:</p>
                        <ul className="list-unstyled">
                          <li>
                            <i
                              className={
                                validation.length
                                  ? "ri-check-line text-success"
                                  : "ri-close-line text-danger"
                              }
                            ></i>
                            <span className="ms-2">
                              At least 8 characters
                            </span>
                          </li>
                          <li>
                            <i
                              className={
                                validation.uppercase
                                  ? "ri-check-line text-success"
                                  : "ri-close-line text-danger"
                              }
                            ></i>
                            <span className="ms-2">
                              At least one uppercase letter
                            </span>
                          </li>
                          <li>
                            <i
                              className={
                                validation.lowercase
                                  ? "ri-check-line text-success"
                                  : "ri-close-line text-danger"
                              }
                            ></i>
                            <span className="ms-2">
                              At least one lowercase letter
                            </span>
                          </li>
                          <li>
                            <i
                              className={
                                validation.digit
                                  ? "ri-check-line text-success"
                                  : "ri-close-line text-danger"
                              }
                            ></i>
                            <span className="ms-2">At least one digit</span>
                          </li>
                          <li>
                            <i
                              className={
                                validation.specialChar
                                  ? "ri-check-line text-success"
                                  : "ri-close-line text-danger"
                              }
                            ></i>
                            <span className="ms-2">
                              At least one special character (e.g., !@#$%^&*)
                            </span>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Photo/Avatar Upload
                      </label>
                      <input
                        type="file"
                        className={`form-control ${formik.touched.image && formik.errors.image
                          ? "is-invalid"
                          : ""
                          }`}
                        id="image"
                        name="image"
                        onChange={handleFileChange}
                        onBlur={formik.handleBlur}
                        accept="image/*"
                      />
                      {formik.touched.image && formik.errors.image ? (
                        <div className="invalid-feedback">
                          {formik.errors.image}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Assign Offices{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="assignedOffices"
                        name="assignedOffices"
                        options={officesOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        value={officesOptions?.filter((option) =>
                          formik.values?.assignedOffices?.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          formik.setFieldValue(
                            "assignedOffices",
                            selectedOptions
                              ? selectedOptions?.map(
                                (option) => option.value
                              )
                              : []
                          )
                        }
                        onBlur={formik.handleBlur}
                      />

                      {formik.touched.assignedOffices &&
                        formik.errors.assignedOffices ? (
                        <div className="text-danger">
                          {/* {formik.errors.assignedOffices} */}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Permissions/Access Level <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="permissions"
                        name="permissions"
                        isMulti
                        options={combinedPermissionsOptions}
                        closeMenuOnSelect={false}
                        value={combinedPermissionsOptions.filter((option) =>
                          formik.values.permissions?.includes(option.value)
                        )}
                        onChange={(selected) =>
                          formik.setFieldValue(
                            "permissions",
                            selected?.map((option) => option.value) || []
                          )
                        }
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.permissions && formik.errors.permissions && (
                        <div className="text-danger">{formik.errors.permissions}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="statuss"
                        name="statuss"
                        required
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.statuss}
                      >
                        <option value="">- Select -</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {formik.touched.statuss && formik.errors.statuss ? (
                        <div className="text-danger">
                          {formik.errors.statuss}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mb-3">
                    {(preview || teammember?.profilePic) && (
                      <div className="mb-3">
                        <label className="form-label">Image Preview</label>
                        <div>
                          <img
                            src={preview || `${process.env.REACT_APP_BASE_URL}${teammember?.profilePic}`}
                            alt="Preview"
                            style={{ maxHeight: "100px" }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>

                      <button className="btn btn-primary submit-btn" type="submit">
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    //   </div>
    // </div>
  );
};

export default SignageBrokerageEditTeammember;
